import { IN_APP } from 'constants/messageMediums';
import { METRIC_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';
import {
  selectIsWebInAppReportingFeatureFlagOn,
  selectUserContextForSegmentTracking,
} from 'core/userContext/selectors';
import {
  CampaignMediumDefaults,
  CampaignMetricLabels,
  OpenMetrics,
} from 'modules/messagingInsights/constants';
import { HiddenCSVLink } from 'modules/productAnalytics/Styled';
import { metricSentenceCase, sortBySelectionOrder } from 'modules/productAnalytics/utils';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { GhostSuccessButton, SecondaryButton } from 'ui/button';
import { BUTTON_SIZES } from 'ui/button/constants';
import { CollapsibleContent } from 'ui/collapsibleContent';
import { ChevronDown16, Export24 } from 'ui/icons';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { MultiFilterSelect } from 'ui/selects';

import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { CAMPAIGN_ANALYTICS_LOCAL_STORAGE_KEY, InAppToBreakdown } from '../constants';
import { useFiltersContext } from '../context';
import { useWhatsAppGAFF } from '../hooks';
import {
  selectAnalyticsMetricsOptions,
  selectCampaign,
  selectCampaignAnalyticsStatsForTimerange,
  selectDestinationsByBreakdownMetrics,
  selectIsLoading,
  selectMessageMedium,
  selectProjectId,
} from '../selectors';
import { PinnedMetricCards } from './PinnedMetricCards';
import { PinnedMetricsEmptyState } from './PinnedMetricsEmptyState';
import {
  AccordionButtonPlaceholder,
  AccordionButtonWrapper,
  CollapseWrapper,
  PinnedMetricsActionsContainer,
  StyledAnalyticsCardGroupContainer,
} from './Styled';

export const PinnedMetrics = ({ setAreOpenMetricsSelected }) => {
  const isWhatsAppGAEnabled = useWhatsAppGAFF();
  const { overviewMetrics, setOverviewMetrics } = useFiltersContext();
  const csvLink = useRef();
  const inAppLink = useRef();
  const isLoading = useSelector(selectIsLoading);
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const messageMedium = useSelector(selectMessageMedium);
  const projectId = useSelector(selectProjectId);
  const campaign = useSelector(selectCampaign);
  const AnalyticsMetricsOptions = useSelector(selectAnalyticsMetricsOptions) || [];
  const destinations = useSelector(selectDestinationsByBreakdownMetrics);
  const results = statsForTimeRange?.results || {};
  const labels = overviewMetrics.map((el) => METRIC_VALUES_TO_LABELS[el]);
  const selectedKeys = labels.slice();
  const userData = useSelector(selectUserContextForSegmentTracking);
  const inAppFF = useSelector(selectIsWebInAppReportingFeatureFlagOn);
  const [, setSelectedMetrics, getSelectedMetrics] = useLocalStorage(
    projectId && messageMedium
      ? `${CAMPAIGN_ANALYTICS_LOCAL_STORAGE_KEY}_${projectId}_${messageMedium}`
      : 'temp_key',
    null,
  );

  const [isCollapsed, setIsCollapsed] = useState(true);
  const collapsibleRef = useRef();

  for (let i = selectedKeys.length % 4; i % 4 !== 0; i += 1) {
    selectedKeys.push(null);
  }

  useEffect(() => {
    if (!messageMedium || !projectId) return;

    const parsedMetrics = getSelectedMetrics();
    const legacyMetrics = JSON.parse(
      localStorage.getItem(`ls.analyticsSelectedMetricObjects_${projectId}_${messageMedium}`) ||
        '[]',
    );
    if (parsedMetrics?.length === 0 && legacyMetrics.length > 0) {
      const legacyMetricsList = legacyMetrics.map((metric) => metric.enumName);
      setOverviewMetrics(legacyMetricsList);
    } else if (parsedMetrics === null) {
      setOverviewMetrics(CampaignMediumDefaults(isWhatsAppGAEnabled)[messageMedium]);
    } else {
      setOverviewMetrics(parsedMetrics);
    }
  }, [messageMedium, projectId]);

  useEffect(() => {
    setAreOpenMetricsSelected(overviewMetrics.some((metric) => OpenMetrics.includes(metric)));
  }, [overviewMetrics]);

  const overflowActions = {
    'export-campaign-metrics-table': () => csvLink?.current?.link?.click(),
    'export-in-app-breakdown': () => inAppLink?.current?.link?.click(),
  };

  const csvDataObject = {
    'Campaign ID': campaign?.id,
    'Campaign Name': campaign?.name,
  };
  selectedKeys.forEach((metric) => {
    if (metric) {
      csvDataObject[metric] = results?.[metric]?.value;
    }
  });

  const destinationObj = {};
  const total = {
    destination: 'Total',
  };
  if (messageMedium === IN_APP && inAppFF) {
    selectedKeys.forEach((metric) => {
      if (destinations[metric]) {
        destinations[metric].forEach((destination) => {
          if (destinationObj[destination.appName]) {
            destinationObj[destination.appName] = {
              ...destinationObj[destination.appName],
              [metric]: destination?.value || 'N/A',
            };
          } else {
            destinationObj[destination.appName] = {
              destination: destination.appName,
              [metric]: destination?.value || 'N/A',
            };
          }
        });
      }
      if (InAppToBreakdown[metric]) {
        total[metric] = results[metric]?.value || 'N/A';
      }
    });
    destinationObj.total = total;
  }

  const destinationCSVArr = Object.values(destinationObj);
  const getOptionsForMetricsDropdown = () => {
    const options = AnalyticsMetricsOptions?.map((metric) => ({
      ...metric,
      label: metricSentenceCase(metric.label),
    }));
    return options;
  };

  return (
    <div>
      <PinnedMetricsActionsContainer>
        <MultiFilterSelect
          content={{
            clearText: 'Clear',
            selectNone: 'Select none',
            selectAll: 'Select all',
          }}
          defaultValues={overviewMetrics}
          placeholder={'0 pinned metrics'}
          inputContent={{ filterPlaceholder: 'Search all metrics' }}
          displayContent={{
            multipleSelectedText: 'pinned metrics',
          }}
          onSelect={(val) => {
            const sortedVal = sortBySelectionOrder(overviewMetrics || [], val);
            setSelectedMetrics(sortedVal);
            setOverviewMetrics(sortedVal);
          }}
          id={`campaign-metrics`}
          title={`campaign-metrics`}
          options={getOptionsForMetricsDropdown()}
          categoryLabels={CampaignMetricLabels}
          isSegmentTracked
          segmentProps={{
            TOGGLE: ['Campaign Analytics Pinned Metrics Clicked', { ...userData }, {}, undefined],
          }}
        />
        {inAppFF && messageMedium === IN_APP ? (
          <Menu
            handleSelectionChange={(id) => id && overflowActions[id]()}
            horizontalAlignment={ALIGNMENT.RIGHT}
            options={[
              {
                label: 'Export pinned metrics',
                id: 'export-campaign-metrics-table',
              },
              {
                label: 'Export pinned in-app metrics by destination',
                id: 'export-in-app-breakdown',
              },
            ]}
          >
            <SecondaryButton size={BUTTON_SIZES.LARGE}>
              <Export24 />
              <span>Export metrics</span> <ChevronDown16 />
            </SecondaryButton>
          </Menu>
        ) : (
          <SecondaryButton
            size={BUTTON_SIZES.LARGE}
            onClick={() => csvLink?.current?.link?.click()}
          >
            <Export24 />
            <span>Export pinned metrics</span>
          </SecondaryButton>
        )}
        <HiddenCSVLink
          data={[csvDataObject]}
          filename={'campaign_metrics_table.csv'}
          ref={csvLink}
          target="_blank"
        />
        {inAppFF && (
          <HiddenCSVLink
            data={destinationCSVArr}
            filename={'in_app_destination_breakdown_table.csv'}
            ref={inAppLink}
            target="_blank"
          />
        )}
      </PinnedMetricsActionsContainer>
      {!isLoading && overviewMetrics.length === 0 && <PinnedMetricsEmptyState />}
      {!isLoading && overviewMetrics.length > 0 && (
        <StyledAnalyticsCardGroupContainer>
          <PinnedMetricCards
            selectedKeys={selectedKeys.slice(0, selectedKeys.length >= 8 ? 8 : selectedKeys.length)}
            results={results}
            messageMedium={messageMedium}
          />
        </StyledAnalyticsCardGroupContainer>
      )}
      {!isLoading && overviewMetrics.length > 8 ? (
        <CollapseWrapper isCollapsed={isCollapsed}>
          <CollapsibleContent isCollapsed={isCollapsed}>
            <StyledAnalyticsCardGroupContainer ref={collapsibleRef}>
              <PinnedMetricCards
                selectedKeys={selectedKeys.slice(8)}
                results={results}
                messageMedium={messageMedium}
              />
            </StyledAnalyticsCardGroupContainer>
          </CollapsibleContent>
          <AccordionButtonWrapper>
            <GhostSuccessButton
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
            >
              {isCollapsed
                ? `Show all ${overviewMetrics.length} pinned metrics`
                : 'Show fewer metrics'}
            </GhostSuccessButton>
          </AccordionButtonWrapper>
        </CollapseWrapper>
      ) : (
        <AccordionButtonPlaceholder />
      )}
    </div>
  );
};
